<script>
import { useConsult } from '@site/composables/useConsult'
import { products, industries } from '@site/data/products'
import AppIcon from '@site/components/common/AppIcon.vue'

export default {
  name: 'HeroSection',
  components: { AppIcon },
  data() {
    const { open } = useConsult()
    return { open, products, industries }
  },
}
</script>

<template>
  <section class="hero">
    <div class="hero__mesh">
      <span class="hero__mesh-blob hero__mesh-blob--1"></span>
      <span class="hero__mesh-blob hero__mesh-blob--2"></span>
      <span class="hero__mesh-blob hero__mesh-blob--3"></span>
    </div>

    <div class="container hero__inner">
      <div class="hero__content">
        <span class="hero__eyebrow">航富通 · 审美新零售数字化管家</span>
        <h1>把好品味<br />做成<em class="text-gradient-set">好生意</em></h1>
        <p class="hero__sub">
          为文创、饰品、潮玩、谷子、服装、珠宝、美妆、手作、杂货门店打造的开单收银与门店管理利器
        </p>
        <div class="hero__cta">
          <button class="btn btn--primary" @click="open">立即咨询</button>
          <router-link :to="products[0].link" class="btn btn--outline">了解航富通</router-link>
          <router-link :to="products[1].link" class="btn btn--outline">了解 Nova</router-link>
        </div>
      </div>

      <div class="hero__visual">
        <div class="hero__device hero__device--pad">
          <div class="hero__device-screen">
            <div class="mock-screen">
              <div class="mock-screen__bar"><span></span><span></span><span></span></div>
              <div class="mock-screen__title">收银台</div>
              <div class="mock-screen__row"><span>潮玩 · 盲盒</span><b>¥ 128</b></div>
              <div class="mock-screen__row"><span>谷子 · 徽章</span><b>¥ 45</b></div>
              <div class="mock-screen__total"><span>合计</span><b>¥ 173</b></div>
              <div class="mock-screen__btn"><AppIcon name="scan" :size="16" /> 扫码开单</div>
            </div>
          </div>
        </div>
        <div class="hero__device hero__device--phone">
          <div class="hero__device-screen">
            <div class="mock-screen mock-screen--phone">
              <div class="mock-screen__bar"><span></span><span></span></div>
              <div class="mock-screen__title small">库存预警</div>
              <div class="hero__phone-item">爆款补货提醒 · 3 件</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 九大行业标签墙 -->
    <div class="container hero__industries">
      <div class="industry-wall reveal">
        <span v-for="ind in industries" :key="ind.name" class="industry-tile" :style="{ '--tile-color': ind.color }">
          <span class="industry-dot"></span>
          {{ ind.name }}
        </span>
      </div>
    </div>

    <div class="hero__scroll-hint">
      <span>向下滑动</span>
      <AppIcon name="chevron" :size="14" />
    </div>
  </section>
</template>


<style lang="scss" scoped>
.hero {
  position: relative;
  min-height: 92vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: $gradient-dark;
  color: $color-white;
  padding: 128px 0 72px;
  overflow: hidden;

  &__mesh {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  &__mesh-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.55;

    &--1 { width: 520px; height: 520px; top: -12%; right: -6%; background: radial-gradient(circle, rgba(168, 85, 247, 0.5), transparent 65%); }
    &--2 { width: 440px; height: 440px; bottom: -8%; left: -8%; background: radial-gradient(circle, rgba(236, 72, 153, 0.42), transparent 62%); }
    &--3 { width: 320px; height: 320px; top: 30%; left: 30%; background: radial-gradient(circle, rgba(251, 146, 60, 0.25), transparent 60%); }
  }

  &__inner {
    position: relative;
    display: grid;
    grid-template-columns: 1.05fr 1fr;
    gap: 56px;
    align-items: center;
    z-index: 1;
  }

  &__eyebrow {
    display: inline-block;
    font-size: 14px;
    letter-spacing: 0.12em;
    color: rgba(255, 255, 255, 0.72);
    margin-bottom: 24px;
    font-weight: $weight-medium;
  }

  h1 {
    font-size: $font-hero;
    font-weight: $weight-heavy;
    line-height: 1.08;
    color: $color-white;
    margin-bottom: 26px;
    letter-spacing: -0.03em;

    em { font-style: normal; }
  }

  &__sub {
    font-size: 19px;
    color: rgba(255, 255, 255, 0.78);
    max-width: 500px;
    line-height: 1.7;
    margin-bottom: 40px;
  }

  &__cta {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
  }

  &__visual {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 440px;
  }

  &__device {
    position: absolute;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.14);
    backdrop-filter: blur(16px) saturate(150%);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.3);

    &--pad {
      width: 440px;
      height: 330px;
      border-radius: 26px;
      transform: rotate(-4deg);
    }

    &--phone {
      width: 180px;
      height: 300px;
      border-radius: 32px;
      right: 40px;
      bottom: -40px;
      transform: rotate(8deg);
      border-width: 1.5px;
    }

    &-screen {
      position: absolute;
      inset: 10px;
      border-radius: 17px;
      overflow: hidden;
    }
  }

  &__phone-item {
    background: rgba(255, 255, 255, 0.94);
    color: $color-text;
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 12px;
    margin: 8px;
    box-shadow: $shadow-sm;
  }

  &__industries {
    position: relative;
    margin-top: 64px;
    z-index: 1;
  }

  &__scroll-hint {
    position: absolute;
    bottom: 26px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.45);
    font-size: 13px;
    animation: bob 2.4s infinite;
    z-index: 2;
  }
}

.mock-screen {
  background: linear-gradient(160deg, #fbfbfd, #f0f2f5);
  color: $color-text;
  width: 100%;
  height: 100%;
  padding: 16px;
  font-size: 14px;

  &--phone { padding: 10px; }

  &__bar { display: flex; gap: 5px; margin-bottom: 12px; span { width: 8px; height: 8px; border-radius: 50%; background: rgba(33,26,46,0.14); } }
  &__title { font-weight: $weight-bold; font-size: 15px; margin-bottom: 12px; &.small { font-size: 13px; } }
  &__row { display: flex; justify-content: space-between; padding: 9px 11px; background: #fff; border-radius: 10px; margin-bottom: 8px; b { color: $color-primary; } box-shadow: $shadow-sm; }
  &__total { display: flex; justify-content: space-between; padding: 11px; font-weight: $weight-bold; b { color: $color-primary; font-size: 18px; } }
  &__btn { display: flex; align-items: center; justify-content: center; gap: 6px; background: $gradient-brand; color: #fff; text-align: center; padding: 10px; border-radius: 11px; font-weight: $weight-semibold; margin-top: 8px; }
}

@keyframes bob {
  0%, 100% { transform: translate(-50%, 0); }
  50% { transform: translate(-50%, -8px); }
}

@media (max-width: $bp-md) {
  .hero__inner { grid-template-columns: 1fr; gap: 24px; }
  .hero h1 { font-size: 48px; }
  .hero__visual { min-height: 360px; order: -1; }
  .hero { padding-top: 108px; }
}

@media (max-width: $bp-sm) {
  .hero h1 { font-size: 38px; }
  .hero__sub { font-size: 17px; }
  .hero__cta { flex-direction: column; align-items: stretch; }
  .hero__device--pad { width: 330px; height: 250px; }
  .hero__device--phone { width: 150px; height: 250px; right: 6px; }
  .hero__industries { margin-top: 40px; }
}
</style>



// WEBPACK FOOTER //
// src/site/components/home/HeroSection.vue